import AutomationToolBenefitsDesktop, { AutomationToolBenefitsProps } from "@components/AutomationToolBenefitsDesktop";
import AutomationToolBenefitsMobile from "@components/AutomationToolBenefitsMobile";
import PaymentStepsAccordion, { PaymentProcess } from "@components/integration/PaymentStepsAccordian";
import PaymentStepsCard, { PaymentSectionDataProps } from "@components/integration/PaymentStepsCard";
import AwardsSection from "@components/new-ui/AwardsSection";
import CaseStudyFeatureSection, { Feature } from "@components/new-ui/CaseStudyFeatureSection";
import ClientLogos from "@components/new-ui/ClientLogos";
import { container_classname } from "@components/new-ui/constants";
import CTASection from "@components/new-ui/CTASection";
import FAQSection, { FAQSectionProps } from "@components/new-ui/FAQSection";
import FeatureInfoCard, { FeatureInfo } from "@components/new-ui/IndustriesSection/FeatureInfoCard";
import Herosection, { HerosectionProps } from "@components/new-ui/IndustriesSection/Herosection";
import IntegrationSection from "@components/new-ui/IntegrationSection";
import NewLayout from "@components/new-ui/Layout";

const featureInfo1: FeatureInfo = {
    mainTitle: "Focus on closing. Let AI handle the rest",
    subtitle2: "Gallabox helps businesses deliver tailored responses to user queries, scheduling appointments, and driving better engagement. This helps businesses:",
    imageUrl: "https://assets.gallabox.com/gb-home/wa-business-solution/message-template.png",
    featureInfoList: ["Focus on sales, not manual follow-ups", "Get back to customers faster", "Book appointments and schedule meetings without manual intervention"],
};

const featureInfo2: FeatureInfo = {
    mainTitle: "Hit peak productivity with an AI-powered compose box",
    subtitle2: "Let your team members do more with less effort with AI. Save their time for more complex customer interactions.",
    imageUrl: "https://assets.gallabox.com/gb-home/wa-business-solution/gitex.png",
    featureInfoList: [
        "Trigger conversation workflows and choose WhatsApp templates instantly",
        "Rewrite with AI, translate in 100+ languages or use canned responses for instant, accurate replies",
        "Transfer chats between individuals, teams, and bots without disrupting user experience.",
    ],
};

const featureInfo3: FeatureInfo = {
    mainTitle: "Manage teams better and assign conversations diligently",
    subtitle2: "Be in control of how you assign incoming WhatsApp messages to different teams.",
    imageUrl: "https://assets.gallabox.com/gb-home/wa-business-solution/manage-teams.png",
    featureInfoList: [
        "Assign teams and roles to users for proper role-clarity and hierarchy across the board",
        "Decide what type of conversations go a particular team based on their expertise or specialization",
        "Ensure your reps get a fair shot at hitting their quotas by automatically balancing workloads across teams.",
    ],
};

const featureInfo4: FeatureInfo = {
    mainTitle: "Skyrocket engagement and boost customer satisfaction",
    subtitle2: "Build custom tailored marketing drip campaigns that convert loyal customers to raving fans of your brand. Use Gallabox to:",
    imageUrl: "https://assets.gallabox.com/gb-home/wa-business-solution/woodpiper.png",
    featureInfoList: ["Push personalized offers", "Collect better feedback", "Generate more referrals"],
};

const caseStudyFeatureCardData: Array<Feature> = [
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/case-study/alrannan-admin-service.png",
        chiptitle: "SERVICE",
        title: "How Alrannan Administrative Services automated sales with Gallabox",
        statsInfoData: [
            { percentage: "45%", label: "Projected quarterly business growth" },
            { percentage: "10%", label: "Increase in team productivity" },
        ],
        url: "https://gallabox.com/case-study/alrannan-administrative-services",
        cardColor: false,
    },
];

const paymentSectionData: PaymentSectionDataProps[] = [
    {
        imageUrl: "https://assets.gallabox.com/gb-home/wa-business-solution/get-gallabox.png",
        title: "Get Gallabox",
        description: "Sign up for a free trial with Gallabox. Key in your business information and activate your free account using your WhatsApp Business API.",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/wa-business-solution/gallabox-account.png",
        title: "Set up your Gallabox account",
        description: "Use the industry-specific templates (or build one from scratch) to create your own chatbots and WhatsApp flows.",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/wa-business-solution/campaign.png",
        title: "Launch your campaigns",
        description: "Once your chatbots have been built and deployed, you can refine your bots to collect better data points and increase user engagement.",
    },
];

const defaultFAQData: FAQSectionProps = {
    title: "FAQs about WhatsApp Business Solution (API) Provider",
    faqItems: [
        {
            question: "Can I automate responses using the WhatsApp Business API?",
            answer: (
                <div>
                    Using a Whatsapp automation tool like Gallabox, you can integrate your calendars - whether its your Google Calendar, or any other one - to your WhatsApp number,
                    and receive meeting updates, reminders, and more.
                    <ul>
                        <li>Run AI-powered chatbots </li>
                        <li>Enable trigger-based automations</li>
                        <li>Integrate their tech stack</li>
                        <li>Send condition-based drip campaigns and broadcast messages</li>
                    </ul>
                </div>
            ),
        },
        {
            question: "How secure is communication through WhatsApp Business API?",
            answer: (
                <>
                    WhatsApp provides users with end-to-end message encryption, so you can be assured of complete data privacy and security. The WhatsApp Business API complies with
                    global data governance laws like GDPR and CCPA, which add another layer of trust to its security standards. Gallabox is also now GDPR and SOC2 compliant, so you
                    can rest assured about your data.
                </>
            ),
        },
        {
            question: "What is the pricing for WhatsApp Business API, and how is it charged?",
            answer: (
                <p>
                    Pricing for the WhatsApp Business API varies by region and can seem a bit complex to understand, but it’s really just a combination of subscription fees and
                    Meta conversation charges.
                    <br />
                    For instance, if you’re running a business in India, and you’re using WhatsApp to communicate with your customers, you would be charged differently based on:
                    <ul>
                        <li>If a user initiates a conversation</li>
                        <li>If you reach out to your users/customers</li>
                        <li>If you’re using a Business Solutions Provider (BSP)</li>
                    </ul>
                    If you want to calculate how much you’d be spending on your WhatsApp Business API provider, use the <a href="">free pricing calculator by Gallabox</a>
                </p>
            ),
        },
        {
            question: "Can I use WhatsApp Business API for marketing purposes?",
            answer: (
                <div>
                    With the WhatsApp Business API, you can handle multiple WhatsApp Business Accounts, but it can become a challenging task. Gallabox makes it simple. Just go to
                    WhatsApp Channel in settings, click Manage Connections, and follow steps to add numbers via 360 Dialog. Register/sign in, verify with OTP, and integrate easily
                    with your CRM.
                </div>
            ),
        },
        {
            question: "Do I need a phone number to use WhatsApp Business API?",
            answer: (
                <div>
                    Yes, you need a dedicated business phone number to use the WhatsApp Business API. The phone number you use must be exclusively for business purposes and cannot
                    be associated with a personal WhatsApp account or used on the WhatsApp Business Mobile App concurrently.
                </div>
            ),
        },
        {
            question: "Can I integrate WhatsApp Business API with CRM or other tools?",
            answer: (
                <>
                    Yes. Integrating your CRM with a WhatsApp Business API tool like Gallabox will help eliminate the risk of integration and syncing downtimes. <br />
                    You could use an external integration solution like Zapier or Make to facilitate a third-party integration between your existing CRM and tech stack with the
                    WhatsApp Business API. But this can be prone to regular failures since the strength of the integration would depend on the strength of the Zapier network.
                </>
            ),
        },
        {
            question: "Is WhatsApp Business API suitable for both small businesses and large enterprises?",
            answer: (
                <p>
                    Definitely! The WhatsApp Business API is very well suited for small-scale businesses to automate query handling and sales orders, while they can focus on the
                    big picture stuff. With chatbots and API integrations, Gallabox also helps organizations scale and manage more complex operations without having to worry about
                    the underlying software.
                </p>
            ),
        },
        {
            question: "Can I send multimedia messages (images, videos, etc.) using WhatsApp Business API?",
            answer: (
                <p>
                    Yes. The WhatsApp Business API does support rich media, handling various media types like images, videos, audio, and documents. You can use all these in
                    combination with your marketing campaigns to drive better engagement and more sales for your organization.
                </p>
            ),
        },
    ],
};

const herosectionData: HerosectionProps = {
    chipTitle: "",
    title: "Build a revenue generation powerhouse with a WhatsApp Business Solution Provider",
    description:
        "Drive quality engagement at scale with Gallabox as your preferred WhatsApp business API provider. Turn your WhatsApp into a powerful lead generation and nurturing engine.",
    primaryButtonText: "Start Free Trial",
    imageUrl: "https://assets.gallabox.com/gb-home/wa-business-solution/hero-image.png",
};

const paymentAccordianData: PaymentProcess = {
    title: "How to integrate Gallabox with your calendar in 3 simple steps?",
    ProcessSteps: [
        {
            imageUrl: "https://assets.gallabox.com/gb-home/wa-business-solution/get-gallabox.png",
            title: "Get Gallabox",
            description: "Sign up for a free trial with Gallabox. Key in your business information and activate your free account using your WhatsApp Business API.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/wa-business-solution/gallabox-account.png",
            title: "Set up your Gallabox account",
            description: "Use the industry-specific templates (or build one from scratch) to create your own chatbots and WhatsApp flows.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/wa-business-solution/campaign.png",
            title: "Launch your campaigns",
            description: "Once your chatbots have been built and deployed, you can refine your bots to collect better data points and increase user engagement.",
        },
    ],
};

const automationToolBenefitsData: AutomationToolBenefitsProps = {
    title: "Benefits of WhatsApp Business Solution (API) provider",
    ProcessSteps: [
        {
            title: "Scale your outreach efforts with you",
            description:
                "With Gallabox as your WhatsApp Business API solution, you can easily scale your marketing and sales operations alongside your business. With security and compliance baked in, Gallabox can support all your growth initiatives at scale without hiccups.",
            imageUrl: "https://assets.gallabox.com/gb-home/wa-business-solution/analytics.png",
        },
        {
            title: "Handle multilingual support at scale",
            description:
                "Build a sales and support channel that works round-the-clock across any region, conversing with potential and existing customers in their choice of language. Gallabox helps support teams cruise on autopilot, and increases customer trust and brand loyalty.",
            imageUrl: "https://assets.gallabox.com/gb-home/wa-business-solution/assist.png",
        },
        {
            title: "Single source of truth across your tech stack",
            description:
                "Gallabox empowers businesses to have a connected ecosystem of products, with a single data source flowing between your entire tech stack — from the CRM to the scheduling tools. You also have advanced APIs and webhooks to make workflows more transparent.",
            imageUrl: "https://assets.gallabox.com/gb-home/wa-business-solution/tech-stack.png",
        },
    ],
};

const WhatsappBusinessApi: React.FC = () => {
    return (
        <NewLayout
            title={"The Best WhatsApp Business API Solution Provider - Gallabox"}
            description={"Turn your WhatsApp into a revenue engine using WhatsApp API integration services and your favourite WhatsApp API tool, Gallabox"}
            canonical={"https://gallabox.com/whatsapp-business-api"}
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/wa-business-solution/wa-business-api.png" }}
            schema={{
                heroOrOgImageAlt: "whatsapp-business-api-image",
                heroOrOgImageDescription:
                    "Drive quality engagement at scale with Gallabox as your preferred WhatsApp business API provider. Turn your WhatsApp into a powerful lead generation and nurturing engine.",
                heroOrOgImageUrl: "https://assets.gallabox.com/gb-home/wa-business-solution/hero-image.png",
                faq: defaultFAQData.faqItems,
                softwareFeatureName: "whatsapp-business-api",
            }}
        >
            <Herosection herosection={herosectionData} />

            <section className="mt-20 mb-20">
                <div className="w-full">
                    <h3 className="text-base text-center font-normal py-2">Trusted by 10,000+ businesses</h3>
                    <ClientLogos />
                </div>
            </section>
            <section className={`${container_classname} mb-20`}>
                <h3 className="mb-5 md:text-6xl text-4xl">Key features and functionalities of WhatsApp Business Solution (API) Provider</h3>
            </section>

            <section>
                <FeatureInfoCard {...featureInfo1} />
                <FeatureInfoCard {...featureInfo2} />
                <FeatureInfoCard {...featureInfo3} />
                <FeatureInfoCard {...featureInfo4} />
            </section>
            <AutomationToolBenefitsDesktop automationToolBenefits={automationToolBenefitsData} />
            <AutomationToolBenefitsMobile automationToolBenefits={automationToolBenefitsData} />
            <section className="lg:block hidden">
                <PaymentStepsCard title={"How to get started with Gallabox’s WhatsApp Business API provider"} paymentSectionData={paymentSectionData} backgroundColor="" />
            </section>
            <PaymentStepsAccordion paymentStepsData={paymentAccordianData} />

            <section>
                <CaseStudyFeatureSection features={caseStudyFeatureCardData} />
            </section>

            <FAQSection faqData={defaultFAQData} />

            <IntegrationSection isLight={true} />
            <AwardsSection />
            <CTASection title="Ready to Elevate Your Marketing Strategy?" description="Experience the power of Gallabox's WhatsApp drip marketing provider firsthand" />
        </NewLayout>
    );
};

export default WhatsappBusinessApi;
